import {
  ErrorOutline as ErrorIcon,
  Star as StarIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
} from "@mui/icons-material";
import {
  Fade,
  IconButton,
  Button as MuiButton,
  Paper,
  Badge,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  Box,
} from "@mui/material";
import * as numbers from "common/numbers";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import StatusBadgeV2 from "components/common/StatusBadgeV2";
import StatusSelect from "components/common/StatusSelectWithChecks";
import Visible from "components/common/Visible";
import { usePricing } from "components/customPricing/forestryModel1/pricingHooks";
import * as labels from "config.labels";
import * as layerSupport from "domain/layerSupport";
import { NONE_PAPER_PROVIDER } from "domain/layerSupport";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { TransitionGroup } from "react-transition-group";
import * as sovService from "services/sovService";
import * as staticDataService from "services/staticDataService";
import * as SOVStore from "store/SOV";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as analyticsStore from "store/analytics";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingSlice from "store/programPricing";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";
import * as utils from "utils";

const FADE_TIMEOUT = 300;

const getValue = (layer, field, calculatedValues, programPricing) => {
  if (field.value)
    return field.value({ layer, programPricing, calculatedValues });
  let rootData;
  switch (field.root) {
    case "programPricing":
      rootData = programPricing?.layers?.[layer.id];
      break;
    case "layer":
      rootData = layer;
      break;
    default:
      rootData = calculatedValues?.values?.layers?.[layer.id];
      break;
  }
  return rootData?.[field.key] ?? "";
};

const buildDataFromFields = (
  fields,
  layers,
  calculatedValues,
  programPricing
) => {
  return layers.reduce((acc, layer) => {
    const dataForLayer = fields.reduce((layerAcc, field) => {
      const value = getValue(layer, field, calculatedValues, programPricing);
      return { ...layerAcc, [field.key]: value };
    }, {});
    return { ...acc, [layer.id]: dataForLayer };
  }, {});
};

const getCellStyle = (layerId, highlightedLayers, isAnyHighlighted) => {
  if (highlightedLayers.includes(layerId)) {
    return {
      borderLeft: "1px solid orange",
      borderRight: "1px solid orange",
    };
  } else if (!isAnyHighlighted) {
    return {};
  }
  return {
    opacity: 0.3,
  };
};

const Coverages = ({ layerId }) => {
  const programPricing = useSelector(programPricingSlice.select);
  const exposedCoverages = programPricing?.binaryCoverages ?? {};
  const allCoverages = (
    programPricing?.coverageConfig?.binaryCoverages ?? []
  ).map(({ key, displayName }) => ({
    displayName,
    exposed: exposedCoverages[key] ?? false,
  }));

  return (
    <Stack
      direction={"column"}
      spacing={0.5}
      alignItems={"center"}
      justifyContent={"center"}
      sx={{
        width: "100%",
      }}
    >
      {allCoverages.map(({ displayName, exposed }) => (
        <Box
          sx={{
            width: "95%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: exposed ? "primary.main" : "secondary.lightest",
            color: exposed ? "white !important" : "black !important",
            borderRadius: "20px",
            textAlign: "center",
            paddingLeft: "5px",
            paddingRight: "5px",
          }}
        >
          {displayName}
        </Box>
      ))}
    </Stack>
  );
};

const useHiddenLayers = ({ layers, inPlayLayerIds }) => {
  const [hiddenLayers, setHiddenLayers] = useState(
    layers.map(({ id }) => id).filter((id) => !inPlayLayerIds.includes(id))
  );

  const clearHiddenLayers = () => {
    setHiddenLayers([]);
  };

  const toggleHiddenLayer = (layerId) => {
    const layer = layers.find((layer) => layer.id === layerId);

    if (!layer) {
      return;
    }

    if (hiddenLayers.includes(layerId)) {
      setHiddenLayers(hiddenLayers.filter((id) => id !== layerId));
      return;
    } else {
      setHiddenLayers([...hiddenLayers, layerId]);
      return;
    }
  };

  return { hiddenLayers, clearHiddenLayers, toggleHiddenLayer };
};

const useHighlightedLayers = ({ hiddenLayers }) => {
  const [highlightedLayers, setHighlightedLayers] = useState([]);

  const toggleHighlight = (layerId) => {
    setHighlightedLayers((prev) =>
      prev.includes(layerId)
        ? prev.filter((id) => id !== layerId)
        : [...prev, layerId]
    );
  };

  const isAnyHighlighted = highlightedLayers.length > 0;

  useEffect(() => {
    setHighlightedLayers((prev) =>
      prev.filter((layerId) => !hiddenLayers.includes(layerId))
    );
  }, [hiddenLayers]);

  return { highlightedLayers, toggleHighlight, isAnyHighlighted };
};

const LayerPricing = ({ showAdditionalInputs, setShowAdditionalInputs }) => {
  const dispatch = useDispatch();
  const calculatedValues = useSelector(analyticsStore.select)?.programPricing;
  const calculatedLayers = calculatedValues?.values?.layers ?? {};
  const layers = useSelector(pricingSelectors.selectResolvedLayers);
  const programPricing = useSelector(programPricingSlice.select);
  const sovInput = useSelector(SOVStore.selectSOV);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const isVisible = useSelector(userSelectors.selectIsVisible);

  const {
    data: resolvedSov,
    isLoading: isResolvedSovFetching,
  } = sovService.useResolveSovQuery({ sovInput, geolocate: true });

  const max_tiv = useMemo(
    () =>
      resolvedSov?.locations
        ?.map(({ TIV }) => TIV)
        ?.reduce((max, curr) => (curr > max ? curr : max), 0) ?? null,
    [resolvedSov?.locations]
  );

  const { isFetching, refetch } = usePricing();

  const inPlayLayerIds = layerSupport.extractInPlayLayerIds(layers);

  const {
    hiddenLayers,
    clearHiddenLayers,
    toggleHiddenLayer,
  } = useHiddenLayers({
    layers,
    inPlayLayerIds,
  });

  const {
    highlightedLayers,
    isAnyHighlighted,
    toggleHighlight,
  } = useHighlightedLayers({
    hiddenLayers,
  });

  const { data: reinsurersConfig } = staticDataService.useConfigQuery(
    "reinsurers"
  );
  const paperProviderMapping = Object.fromEntries(
    (reinsurersConfig?.reinsurers ?? []).map(({ key, name }) => [key, name])
  );

  const fields = [
    {
      key: "paperProvider",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_PAPER_PROVIDER_LABEL,
        {
          default: "Paper Provider",
        }
      ),
      renderCell: ({ layer }) => {
        const value = layer.paperProvider;
        const display =
          value === NONE_PAPER_PROVIDER
            ? "None"
            : paperProviderMapping?.[value] ?? value;
        return <Typography>{display}</Typography>;
      },
      visible: true,
    },
    {
      key: "layerLoss",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_TECHNICAL_LABEL, {
        default: "Technical",
      }),
      root: "layerPricing",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "rol",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_ROL_LABEL, {
        default: "ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "marginalLossRatio",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_MARGINAL_LOSS_RATIO_LABEL,
        {
          default: "Marginal Loss Ratio",
        }
      ),
      root: "layer",
      valueType: "percentage",
      onChange: "updateLayer",
      readOnly: false,
      visible: true,
    },
    {
      key: "layerNetAdequate",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_NET_ADEQUATE_LABEL, {
        default: "Net Adequate Premium",
      }),
      root: "layerPricing",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "layerGrossActual",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_GROSS_ACTUAL_LABEL, {
        default: "Actual Gross Premium",
      }),
      root: "layerPricing",
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "brokerTargetGrossPremium",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_BROKER_TARGET_LABEL, {
        default: "Broker Target",
      }),
      root: "layer",
      valueType: "currency",
      onChange: "updateLayer",
      readOnly: false,
      visible: true,
    },
    {
      key: "grossRateTarget",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_BROKER_GROSS_RATE_LABEL,
        {
          default: "Gross Rate",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 3,
      readOnly: true,
      visible: true,
    },
    {
      key: "brokerActualGrossRate",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_BROKER_ACTUAL_GROSS_RATE_LABEL,
        {
          default: "Broker Actual Gross Rate",
        }
      ),
      root: "layer",
      valueType: "percentage",
      dp: 4,
      onChange: "updateLayer",
      readOnly: false,
      visible: true,
    },
    {
      key: "adequacyTarget",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_BROKER_ADEQUACY_LABEL,
        {
          default: "Broker Adequacy",
        }
      ),
      root: "layerPricing",
      valueType: "float",
      dp: 0,
      suffix: "%",
      readOnly: true,
      visible: true,
    },
    {
      key: "rolTarget",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_ROL_TARGET_LABEL, {
        default: "Broker Target ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "quotedGrossPremium",
      root: "layer",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_OUR_QUOTE_LABEL, {
        default: "Our Quote",
      }),
      onChange: "updateLayer",
      valueType: "currency",
      readOnly: false,
      visible: true,
    },
    {
      key: "grossRateQuote",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_OUR_QUOTE_GROSS_RATE_LABEL,
        {
          default: "Gross Rate",
        }
      ),
      root: "layerPricing",
      valueType: "percentage",
      dp: 3,
      readOnly: true,
      visible: true,
    },
    {
      key: "quotedActualGrossRate",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_QUOTE_ACTUAL_GROSS_RATE_LABEL,
        {
          default: "Quote Actual Gross Rate",
        }
      ),
      root: "layer",
      valueType: "percentage",
      dp: 4,
      onChange: "updateLayer",
      readOnly: false,
      visible: true,
    },
    {
      key: "adequacyQuote",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_QUOTE_ADEQUACY_LABEL,
        {
          default: "Quote Adequacy",
        }
      ),
      root: "layerPricing",
      valueType: "float",
      dp: 0,
      suffix: "%",
      readOnly: true,
      visible: true,
    },
    {
      key: "rolQuote",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_OUR_QUOTE_ROL_LABEL, {
        default: "Our Quote ROL",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "brokerage",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_BROKERAGE_LABEL, {
        default: "Brokerage",
      }),
      root: "layer",
      valueType: "float",
      dp: 2,
      suffix: "%",
      readOnly: true,
      visible: true,
    },
    {
      key: "quotedNetPremium",
      root: "layerPricing",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_OUR_QUOTE_NET_LABEL, {
        default: "Our Quote Net",
      }),
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "grossPremium",
      root: "layer",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_FOT_LABEL, {
        default: "FOT",
      }),
      onChange: "updateLayer",
      valueType: "currency",
      readOnly: false,
      visible: true,
    },
    {
      key: "grossRateFot",
      root: "layerPricing",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_FOT_GROSS_RATE_LABEL,
        {
          default: "Gross Rate",
        }
      ),
      valueType: "percentage",
      dp: 3,
      readOnly: true,
      visible: true,
    },
    {
      key: "rolFot",
      root: "layerPricing",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_FOT_ROL_LABEL, {
        default: "FOT ROL",
      }),
      valueType: "percentage",
      dp: 2,
      readOnly: true,
      visible: true,
    },
    {
      key: "netPremium",
      root: "layerPricing",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_FOT_NET_LABEL, {
        default: "FOT Net",
      }),
      valueType: "currency",
      readOnly: true,
      visible: true,
    },
    {
      key: "guLoss",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_TOTAL_EXPECTED_LOSS_LABEL,
        {
          default: "Total Expected Loss",
        }
      ),
      root: "layerPricing",
      valueType: "currency",
      dp: 0,
      readOnly: true,
      visible: true,
    },
    {
      key: "limitLoad",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_LIMIT_LOAD_LABEL, {
        default: "Limit Load",
      }),
      root: "layerPricing",
      valueType: "float",
      dp: 3,
      readOnly: true,
      visible: true,
    },
    {
      key: "deductibleLoad",
      label: translateLabel(
        labels.FORESTRY_LAYER_PRICING_DEDUCTIBLE_LOAD_LABEL,
        {
          default: "Deductible Load",
        }
      ),
      root: "layerPricing",
      valueType: "float",
      dp: 3,
      readOnly: true,
      visible: true,
    },
    {
      key: "rate",
      label: translateLabel(labels.FORESTRY_LAYER_PRICING_RATE_LABEL, {
        default: "Location Rate",
      }),
      root: "layerPricing",
      valueType: "percentage",
      dp: 3,
      readOnly: true,
      visible: true,
    },
  ].filter(
    ({ key }) => isVisible(`<ForestryLayerPricing>.layerPricing.${key}`) ?? true
  );

  const fieldData = buildDataFromFields(
    fields,
    layers,
    calculatedValues,
    programPricing
  );

  const renderCell = (layer, field) => {
    if (field.renderCell) {
      return field.renderCell({ layer });
    }

    const updateProgramPricing = (layerId, key, value) => {
      const updatedLayers = programPricing?.layers ?? {};
      const currentLayer = updatedLayers[layerId] ?? {};
      dispatch(
        programPricingSlice.update({
          layers: {
            ...updatedLayers,
            [layerId]: {
              ...currentLayer,
              [key]: value,
            },
          },
        })
      );
    };
    const updateLayer = (layerId, key, value) => {
      const values = {
        [key]: value,
      };
      if (key === "lineSize") {
        values.shareOfLine = null;
      }

      dispatch(
        pricingActions.updateLayer({
          layerId: layerId,
          values,
        })
      );
    };
    const value = fieldData[layer.id]?.[field.key] ?? null;
    if (field.readOnly) {
      if (value == null || value === "") {
        return <></>;
      } else if (field.valueType === "percentage") {
        return (
          <Tooltip title={value}>
            <Typography>{numbers.percentage(field.dp ?? 3)(value)}</Typography>
          </Tooltip>
        );
      } else if (field.valueType === "currency") {
        return (
          <Tooltip title={value}>
            <Typography>{numbers.currencyFormat(value)}</Typography>
          </Tooltip>
        );
      } else {
        return (
          <Tooltip title={value}>
            <Typography>
              {numbers.dpString(field.dp ?? 0, { suffix: field.suffix })(value)}
            </Typography>
          </Tooltip>
        );
      }
    }
    return (
      <Disabled ifReadOnly>
        <PrettyNumberTextField
          type={"text"}
          size={"small"}
          hiddenLabel
          suffix={field.valueType === "percentage" ? "%" : ""}
          prefix={field.valueType === "currency" ? "$" : ""}
          InputLabelProps={{ shrink: true }}
          inputProps={{
            style: {
              textAlign: "right",
            },
          }}
          value={value}
          onChangeNumber={
            field.onChange === "updateLayer"
              ? (value) => updateLayer(layer.id, field.key, value)
              : (value) => updateProgramPricing(layer.id, field.key, value)
          }
          variant={"filled"}
          readOnly
        />
      </Disabled>
    );
  };

  const maxTivPercentages = useMemo(() => {
    return layers.reduce((acc, layer) => {
      if (isResolvedSovFetching) {
        acc[layer.id] = translateLabel(
          labels.FORESTRY_LAYER_PRICING_MAX_TIV_PERCENTAGE_LOADING_MESSAGE,
          {
            default: "Loading...",
          }
        );
      } else if (!max_tiv) {
        acc[layer.id] = translateLabel(
          labels.FORESTRY_LAYER_PRICING_MAX_TIV_PERCENTAGE_NO_SOV_MESSAGE,
          {
            default: "No SOV data",
          }
        );
      } else if (!layer?.attachment) {
        acc[layer.id] = translateLabel(
          labels.FORESTRY_LAYER_PRICING_MAX_TIV_PERCENTAGE_NO_ATTACHMENT_MESSAGE,
          {
            default: "No attachment point",
          }
        );
      } else {
        const percentage = ((layer.attachment / max_tiv) * 100).toFixed(0);
        acc[layer.id] = {
          prefix: translateLabel(
            labels.FORESTRY_LAYER_PRICING_MAX_TIV_PERCENTAGE_PREFIX_MESSAGE,
            {
              default: "As % of top loc:",
            }
          ),
          value: `${percentage}%`,
        };
      }
      return acc;
    }, {});
  }, [layers, max_tiv, isResolvedSovFetching, translateLabel]);

  const MaxTivAttachmentPercentage = ({ percentage }) => {
    if (typeof percentage === "string") {
      return <Typography fontSize={"0.8rem"}>{percentage}</Typography>;
    }

    return (
      <Box sx={{ display: "inline-flex" }}>
        <Typography fontSize={"0.8rem"}>{percentage.prefix}</Typography>
        <Typography fontSize={"0.8rem"} sx={{ fontWeight: "bold" }}>
          {percentage.value}
        </Typography>
      </Box>
    );
  };

  const renderRow = (field, index) => {
    const rowContent = (
      <TableRow key={index}>
        <TableCell align={"right"} sx={{ minWidth: "200px" }}>
          <Typography fontSize={"0.7rem"}>{field.label}</Typography>
        </TableCell>
        <TransitionGroup component={null}>
          {layers
            .filter((layer) => !hiddenLayers.includes(layer.id))
            .map((layer) => (
              <Fade key={layer.id} timeout={FADE_TIMEOUT} appear={false}>
                <TableCell
                  align={"right"}
                  style={{
                    ...getCellStyle(
                      layer.id,
                      highlightedLayers,
                      isAnyHighlighted
                    ),
                  }}
                >
                  {renderCell(layer, field)}
                </TableCell>
              </Fade>
            ))}
        </TransitionGroup>
        <TableCell>{/* Extra space */}</TableCell>
      </TableRow>
    );

    if (field.visibilityTag) {
      return (
        <Visible
          byTag={field.visibilityTag}
          defaultVisible={field.defaultVisible}
          key={index}
        >
          {rowContent}
        </Visible>
      );
    }

    return rowContent;
  };

  return (
    <Component
      title={translateLabel(labels.FORESTRY_LAYER_PRICING_TITLE, {
        default: "Summary",
      })}
      isFetching={isFetching}
      options={
        <Stack direction={"row"} spacing={2} alignItems={"center"}>
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <MuiButton
              onClick={() => clearHiddenLayers()}
              variant={"text"}
              disabled={hiddenLayers.length === 0}
              sx={{
                m: 1,
                display: "flex",
                width: "150px",
                justifyContent: "flex-end",
                padding: "0 16px",
              }}
              endIcon={
                <VisibilityIcon
                  size={"m"}
                  color={hiddenLayers.length > 0 ? "primary" : "disabled"}
                />
              }
            >
              {hiddenLayers.length > 0 && (
                <Typography fontSize={"0.8rem"} sx={{ mr: 1 }}>
                  {hiddenLayers.length} {"hidden"}
                </Typography>
              )}
            </MuiButton>
          </Stack>
          <Disabled ifReadOnly>
            <Button onClick={refetch} isLoading={isFetching}>
              {translateLabel(
                labels.FORESTRY_LAYER_PRICING_CALCULATE_BUTTON_MESSAGE,
                {
                  default: "Calculate",
                }
              )}
            </Button>
          </Disabled>
        </Stack>
      }
    >
      <Stack direction={"column"} spacing={2}>
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell align={"right"} width={200}></TableCell>
                <TransitionGroup component={null}>
                  {layers
                    .filter((layer) => !hiddenLayers.includes(layer.id))
                    .map((layer) => {
                      const errors = calculatedLayers[layer.id]?.error;
                      const errorCount = errors?.length || 0;
                      const errorMessage = errors
                        ? errors.map((error) => error.message).join(", ")
                        : "";

                      return (
                        <Fade
                          key={layer.id}
                          timeout={FADE_TIMEOUT}
                          appear={false}
                        >
                          <TableCell
                            align={"right"}
                            width={200}
                            sx={{
                              mt: 1,
                              "&:hover .hover-icons": {
                                visibility: "visible",
                              },
                            }}
                          >
                            <Stack direction={"column"} spacing={0}>
                              <Stack
                                direction={"row"}
                                spacing={0}
                                justifyContent={"center"}
                                alignItems={"center"}
                                sx={{
                                  ml: 1,
                                  minHeight: "40px",
                                }}
                              >
                                <IconButton
                                  onClick={() => toggleHighlight(layer.id)}
                                  className={"hover-icons"}
                                  sx={{
                                    ml: 1,
                                    visibility: "hidden",
                                  }}
                                >
                                  <StarIcon />
                                </IconButton>
                                <IconButton
                                  onClick={() => toggleHiddenLayer(layer.id)}
                                  className={"hover-icons"}
                                  sx={{
                                    ml: 1,
                                    visibility: "hidden",
                                  }}
                                >
                                  <VisibilityOffIcon />
                                </IconButton>
                              </Stack>
                              <Stack
                                direction={"row"}
                                spacing={1}
                                justifyContent={"center"}
                                alignItems={"center"}
                              >
                                <Visible
                                  byTag={
                                    "<ForestryLayerPricing>.layerPricing.topLocTIVPercentage"
                                  }
                                  defaultVisible={true}
                                >
                                  <MaxTivAttachmentPercentage
                                    percentage={maxTivPercentages[layer.id]}
                                  />
                                </Visible>
                              </Stack>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "center",
                                }}
                              >
                                <Tooltip title={layer.status}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <StatusBadgeV2
                                        status={layer.status}
                                        variant={"icon"}
                                      />
                                    </Box>
                                    <Typography
                                      fontSize={"0.8rem"}
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      {utils.shortLayerName(layer)}
                                    </Typography>
                                    <Visible
                                      byTag={
                                        "<ForestryLayerPricing>.layerPricing.statusSelector"
                                      }
                                      defaultVisible={true}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        <StatusSelect
                                          layer={layer}
                                          variant={"switch"}
                                          status={layer?.status ?? null}
                                          onChange={(status) => {
                                            dispatch(
                                              pricingActions.updateLayer({
                                                layerId: layer.id,
                                                values: { status },
                                              })
                                            );
                                          }}
                                        />
                                      </Box>
                                    </Visible>
                                  </Box>
                                </Tooltip>
                              </Box>
                              {/* Error Badge with Tooltip */}
                              {errorCount > 0 && (
                                <Stack
                                  direction={"row"}
                                  spacing={1}
                                  alignItems={"center"}
                                  justifyContent={"center"}
                                >
                                  <Tooltip title={errorMessage}>
                                    <Badge
                                      badgeContent={errorCount}
                                      color={"error"}
                                    >
                                      <ErrorIcon
                                        variant={"outlined"}
                                        color={"error"}
                                      />
                                    </Badge>
                                  </Tooltip>
                                </Stack>
                              )}
                            </Stack>
                          </TableCell>
                        </Fade>
                      );
                    })}
                </TransitionGroup>
                <TableCell>{/* Extra space */}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell align={"right"} sx={{ minWidth: "200px" }}>
                  <Typography fontSize={"0.7rem"}>
                    {translateLabel(
                      labels.FORESTRY_LAYER_PRICING_PERILS_LABEL,
                      {
                        default: "Perils",
                      }
                    )}
                  </Typography>
                </TableCell>
                <TransitionGroup component={null}>
                  {layers
                    .filter((layer) => !hiddenLayers.includes(layer.id))
                    .map((layer) => (
                      <Fade
                        key={layer.id}
                        timeout={FADE_TIMEOUT}
                        appear={false}
                      >
                        <TableCell
                          align={"right"}
                          style={getCellStyle(
                            layer.id,
                            highlightedLayers,
                            isAnyHighlighted
                          )}
                        >
                          <Coverages layerId={layer.id} />
                        </TableCell>
                      </Fade>
                    ))}
                </TransitionGroup>
                <TableCell>{/* Extra space */}</TableCell>
              </TableRow>
              {fields
                .filter((field) => field.visible)
                .map((field, index) => renderRow(field, index))}
            </TableBody>
          </Table>
        </TableContainer>
      </Stack>
    </Component>
  );
};

export default LayerPricing;
